import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { Spinner } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";

const STATUS = { paid: "Paid", partially_refunded: "Paid · partly refunded", refunded: "Refunded" };
const Line = ({ label, value, strong, testid }) => (
  <div className={`flex justify-between gap-4 py-2 ${strong ? "border-t pt-3 font-semibold" : ""}`}>
    <span>{label}</span><span className="font-mono-num" data-testid={testid}>{value}</span>
  </div>
);

// Itemized guest receipt; the browser's print dialog also saves it as a PDF.
export default function Receipt() {
  const { bookingId } = useParams();
  const { data: r, isLoading, error } = useQuery({ queryKey: ["receipt", bookingId], queryFn: () => api.get(`/bookings/${bookingId}/receipt`).then((x) => x.data), retry: false });
  if (isLoading) return <Spinner />;
  if (error) return <p className="py-20 text-center text-stone-600" data-testid="receipt-error">{errMsg(error)}</p>;
  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6" data-testid="receipt-page">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <Link to="/trips" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-primary" data-testid="receipt-back-link"><ArrowLeft className="h-4 w-4" />Trips</Link>
        <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="receipt-print-button"><Printer className="mr-2 h-4 w-4" />Print or save PDF</Button>
      </div>
      <div className="rounded-3xl border bg-white p-6 text-sm sm:p-8 print:border-0 print:p-0">
        {r.is_test && <p className="mb-4 rounded-xl bg-amber-100 p-2 text-center text-xs font-bold text-amber-800" data-testid="receipt-test-banner">TEST BOOKING · Stripe test mode, no real charge</p>}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-2">
          <div><p className="text-2xl font-bold">FairStay receipt</p><p className="text-stone-500" data-testid="receipt-number">{r.receipt_number}</p></div>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid="receipt-status">{STATUS[r.payment_status] || r.payment_status}</span>
        </div>
        <p className="text-lg font-semibold" data-testid="receipt-property">{r.property.title}</p>
        <p className="text-stone-600">{r.property.location}</p>
        <p className="mt-1 text-stone-600" data-testid="receipt-dates">{fmtDate(r.check_in)} → {fmtDate(r.check_out)} · {r.nights} night{r.nights === 1 ? "" : "s"} · {r.guests} guest{r.guests === 1 ? "" : "s"} · {r.guest_name}</p>
        <div className="mt-6 divide-y-0">
          <Line label={`Lodging · ${r.nights} nights`} value={money(r.nightly_total)} />
          {r.cleaning_fee > 0 && <Line label="Cleaning fee" value={money(r.cleaning_fee)} />}
          <Line label="Rental subtotal" value={money(r.rental_subtotal)} strong testid="receipt-subtotal" />
          {r.taxes.map((t) => <Line key={t.name} label={`${t.name} (${t.rate}%)`} value={money(t.amount)} />)}
          <Line label="Lodging taxes" value={money(r.tax_total)} testid="receipt-tax-total" />
          <Line label="FairStay guest service fee" value={money(0)} />
          <Line label="Total paid" value={money(r.total_paid)} strong testid="receipt-total" />
          {r.refunds.map((x) => <Line key={x.refund_id} label={`Refund · ${fmtDate(x.at)}`} value={`−${money(x.amount)}`} />)}
          {r.refunded_amount > 0 && <Line label="Net paid after refunds" value={money(r.net_paid)} strong testid="receipt-net" />}
        </div>
        <div className="mt-6 space-y-1 text-xs text-stone-500">
          <p>Paid {r.paid_at ? fmtDate(r.paid_at) : ""} · Transaction reference <span className="break-all font-mono-num" data-testid="receipt-reference">{r.transaction_reference}</span></p>
          <p>{r.cancellation_policy}</p>
          <p>Lodging taxes are calculated from the home's location and shown separately from the rent. They are not part of FairStay's fee.</p>
        </div>
      </div>
    </div>
  );
}
